/*
 * Toolbar for any paginated/searchable table. Drops on top of a table
 * paired with `useTableState`. Provides:
 *   - search input (debounced via the hook's setSearch)
 *   - page size selector
 *   - prev/next + "page X of Y" indicator
 *   - export menu (CSV / XLSX / JSON / PDF / DOCX)
 *
 * Fully controlled — caller passes the relevant slice of the
 * useTableState result + the rows-to-export and column metadata. Keep
 * this component pure-presentational so future tables can adopt without
 * coupling to a specific data source.
 */

import { useState } from "react";
import {
  EXPORT_FORMATS,
  ExportFormat,
  ExportableRow,
  exportRows
} from "../lib/tableExport.js";

export interface TableToolbarProps {
  search: string;
  setSearch: (s: string) => void;
  page: number;
  setPage: (p: number) => void;
  totalPages: number;
  totalRows: number;
  pageSize: number;
  setPageSize: (n: number) => void;
  exportFilename?: string;
  exportRowsData?: ExportableRow[];
  exportColumns?: string[];
  exportColumnLabels?: Record<string, string>;
  exportTitle?: string;
  searchPlaceholder?: string;
  // Hide the Export ▾ menu (pagination-only mode). Use for tables where
  // exports aren't useful (small lookup tables, drawers, etc.). When set,
  // the export* props above can be omitted entirely.
  hideExport?: boolean;
}

const PAGE_SIZE_CHOICES = [10, 25, 50, 100, 250];

export default function TableToolbar({
  search,
  setSearch,
  page,
  setPage,
  totalPages,
  totalRows,
  pageSize,
  setPageSize,
  exportFilename,
  exportRowsData,
  exportColumns,
  exportColumnLabels,
  exportTitle,
  searchPlaceholder,
  hideExport
}: TableToolbarProps) {
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleExport = async (format: ExportFormat) => {
    if (!exportFilename || !exportColumns || !exportRowsData) return;
    setExporting(format);
    setMenuOpen(false);
    try {
      await exportRows(format, {
        filename: exportFilename,
        columns: exportColumns,
        rows: exportRowsData,
        title: exportTitle,
        columnLabels: exportColumnLabels
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="ts-toolbar">
      <input
        type="search"
        className="ts-toolbar-search"
        placeholder={searchPlaceholder ?? "Search…"}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <span className="ts-toolbar-stats">
        {totalRows.toLocaleString()} {totalRows === 1 ? "row" : "rows"}
      </span>
      <div className="ts-toolbar-pager">
        <button
          type="button"
          className="ts-toolbar-btn"
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span className="ts-toolbar-pageno">
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="ts-toolbar-btn"
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
      <select
        className="ts-toolbar-select"
        value={pageSize}
        onChange={(e) => setPageSize(Number(e.target.value))}
        aria-label="Rows per page"
      >
        {PAGE_SIZE_CHOICES.map((n) => (
          <option key={n} value={n}>
            {n}/page
          </option>
        ))}
      </select>
      {hideExport ? null : (
        <div className="ts-toolbar-export" onMouseLeave={() => setMenuOpen(false)}>
          <button
            type="button"
            className="ts-toolbar-btn ts-toolbar-export-trigger"
            onClick={() => setMenuOpen((o) => !o)}
            disabled={exporting !== null || !exportRowsData || exportRowsData.length === 0}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            {exporting ? `Exporting ${exporting}…` : "Export ▾"}
          </button>
          {menuOpen ? (
            <div className="ts-toolbar-menu" role="menu">
              {EXPORT_FORMATS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="menuitem"
                  className="ts-toolbar-menu-item"
                  onClick={() => void handleExport(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
